"use strict";
var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
/* A library for working with SockJS websockets.
* This is somewhat based on rxjs-websockets but with a lot of customisations
* for freelancer.
*/
import { Inject, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import { Auth } from '@shared/auth';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { async } from 'rxjs/scheduler/async';
import { Subject } from 'rxjs/Subject';
import { DATASTORE_CONFIG } from '../datastore.config';
import { DatastoreConfig } from '../datastore.interface';
import { enterZone, leaveZone } from '../zone-util';
import { MultiSet } from './multiset';
import { isSockMessageServerEvent, } from './server-message-event.model';
import { generateChannelSubMessage, generateHeader, generateOnlineOfflineSub, generateOnlineOfflineUnsub, Socket, } from './sock-js';
var WebSocketService = (function () {
    function WebSocketService(datastoreConfig, auth, store, ngZone) {
        this.datastoreConfig = datastoreConfig;
        this.auth = auth;
        this.store = store;
        this.ngZone = ngZone;
        this.connectionStatus = new BehaviorSubject(0);
        this.fromServerStream = new Subject();
        this.onlineOfflineSubs = new Subject();
        this.open = false;
        this.channelSubscriptions = new Set();
        this.onlineOfflineSubscriptions = new MultiSet();
        this.create();
    }
    WebSocketService.prototype.create = function () {
        var _this = this;
        this.auth.authState.distinctUntilChanged().subscribe(function (auth) {
            _this.open = false;
            // FIXME: handle logged-out / logging-out
            if (!auth) {
                return;
            }
            var closed = function () {
                if (!_this.open) {
                    return;
                }
                _this.connectionStatus.next(_this.connectionStatus.getValue() - 1);
                if (_this.onlineOfflineSubscription) {
                    _this.onlineOfflineSubscription.unsubscribe();
                }
                _this.open = false;
            };
            _this.sock = new Socket(_this.datastoreConfig.webSocketUrl);
            _this.sock.onopen = function () {
                _this.open = true;
                _this.connectionStatus.next(_this.connectionStatus.getValue() + 1);
                _this.sendEvent(generateHeader(auth));
                _this.subscribeToEvents();
            };
            // Only send events with actual messages, not handshaking messages.
            _this.sock.onmessage = function (event) {
                var decodedEvent = JSON.parse(event.data);
                if (isSockMessageServerEvent(decodedEvent)) {
                    _this.processEvent(decodedEvent, auth.userId);
                }
            };
            _this.sock.onclose = function (event) {
                closed();
                if (event.wasClean) {
                    _this.fromServerStream.complete();
                }
                else {
                    // FIXME: handle disconnections / retry
                    // e.g., window.setTimeout(() => this.create(), 1000);
                    // FIXME: use global error toast service to display something when we
                    // couln't reconnect.
                    console.error(new Error(event.reason));
                }
            };
            return function () {
                if (_this.sock) {
                    closed();
                    _this.sock.close();
                }
            };
        });
    };
    WebSocketService.prototype.sendEvent = function (message) {
        if (this.open) {
            this.sock.send(message);
        }
        else {
            console.error('Tried sending a message to a closed websocket.');
        }
    };
    WebSocketService.prototype.processEvent = function (event, toUserId) {
        var action = {
            type: 'WS_MESSAGE',
            payload: __assign({}, event.body, { toUserId: toUserId }),
        };
        this.store.dispatch(action);
        // Once the store is updated then we can let everyone know.
        this.fromServerStream.next(event);
    };
    /* We buffer the user subscriptions and unsubscriptions by 1 second
     * to reduce backend load.
     * It's possible to have subscribed and unsubscriped within that buffer
     * so we remove such sub/unsub (and reverse) manually.
     */
    WebSocketService.prototype.subscribeToEvents = function () {
        var _this = this;
        // Subscribe to existing subscriptions
        var existingSubscriptions = this.onlineOfflineSubscriptions.values();
        if (existingSubscriptions.length) {
            this.sendEvent(generateOnlineOfflineSub(existingSubscriptions));
        }
        if (this.channelSubscriptions.size) {
            this.sendEvent(generateChannelSubMessage(Array.from(this.channelSubscriptions)));
        }
        // Subscribe to any new ones that come in
        this.onlineOfflineSubscription = this.onlineOfflineSubs
            .bufferTime(1000, leaveZone(this.ngZone, async))
            .observeOn(enterZone(this.ngZone, async))
            .subscribe(function (userIds) {
            var reducedSubscriptions = userIds.reduce(function (acc, current) {
                var subs = acc.subs, unsubs = acc.unsubs;
                if (current.type === 'sub') {
                    if (acc.unsubs.includes(current.id)) {
                        unsubs = acc.unsubs.filter(function (id) { return id !== current.id; });
                    }
                    else {
                        subs = acc.subs.concat([current.id]);
                    }
                }
                if (current.type === 'unsub') {
                    if (acc.subs.includes(current.id)) {
                        subs = acc.subs.filter(function (id) { return id !== current.id; });
                    }
                    else {
                        unsubs = acc.unsubs.concat([current.id]);
                    }
                }
                return { subs: subs, unsubs: unsubs };
            }, { subs: [], unsubs: [] });
            if (_this.open) {
                if (reducedSubscriptions.subs.length) {
                    _this.sendEvent(generateOnlineOfflineSub(reducedSubscriptions.subs));
                }
                if (reducedSubscriptions.unsubs.length) {
                    _this.sendEvent(generateOnlineOfflineUnsub(reducedSubscriptions.unsubs));
                }
            }
        });
    };
    WebSocketService.prototype.subscribeOnlineOffline = function (userId) {
        var _this = this;
        // Watch the user id stream, calculate watch for changes,
        // subscribe/unsubscribe to any additions/removals respectively
        // and return the user status for the user who is subscribed to.
        // We add `undefined` to the end so we can get the last unsub.
        return userId
            .distinctUntilChanged()
            .concat(Observable.of(undefined))
            .scan(function (acc, next) { return ({
            current: next,
            previous: acc.current,
        }); }, {
            current: undefined,
            previous: undefined,
        })
            .map(function (_a) {
            var previous = _a.previous, current = _a.current;
            if (previous) {
                _this.unsubscribeUserOnlineOffline(previous);
            }
            if (current) {
                _this.subscribeUserOnlineOffline(current);
            }
            return current;
        });
    };
    WebSocketService.prototype.subscribeUserOnlineOffline = function (userId) {
        // If this user id is new, subscribe.
        if (this.onlineOfflineSubscriptions.multiplicity(userId) === 0) {
            this.onlineOfflineSubs.next({ type: 'sub', id: userId });
        }
        this.onlineOfflineSubscriptions.add(userId);
    };
    WebSocketService.prototype.unsubscribeUserOnlineOffline = function (userId) {
        // If this user id is the last one, unsubscribe.
        if (this.onlineOfflineSubscriptions.multiplicity(userId) === 1) {
            this.onlineOfflineSubs.next({ type: 'unsub', id: userId });
        }
        this.onlineOfflineSubscriptions.remove(userId);
    };
    WebSocketService.prototype.setChannels = function (channels) {
        var currentSubscriptions = Array.from(this.channelSubscriptions);
        var arrayDiff = function (a, b) {
            return a.filter(function (x) { return !b.includes(x); });
        };
        this.unsubscribeChannels(arrayDiff(currentSubscriptions, channels));
        this.subscribeChannels(arrayDiff(channels, currentSubscriptions));
    };
    WebSocketService.prototype.subscribeChannels = function (channels) {
        var _this = this;
        if (channels.length) {
            if (this.open) {
                this.sendEvent(generateChannelSubMessage(channels));
            }
            channels.forEach(function (channel) { return _this.channelSubscriptions.add(channel); });
        }
    };
    WebSocketService.prototype.unsubscribeChannels = function (channels) {
        var _this = this;
        if (channels.length) {
            channels.forEach(function (channel) { return _this.channelSubscriptions.delete(channel); });
            /* TODO: Do this properly.
             * Unfortunately the websocket backend doesn't support unsubscribing
             * and so we need to disconnect and reconnect the websocket to unsub :(
             *
             * Ref T35544
             */
            if (this.unsubDebounceTime) {
                clearTimeout(this.unsubDebounceTime);
            }
            this.unsubDebounceTime = window.setTimeout(function () {
                _this.unsubDebounceTime = undefined;
                _this.sock.close();
                window.setTimeout(function () { return _this.create(); }, 1000);
                // We should also disconnect the other websocket that libnotify has.
                var w = window;
                if (w.libnotify && w.libnotify.close) {
                    w.libnotify.close();
                }
            }, 5000);
        }
    };
    WebSocketService.ctorParameters = function () { return [{ type: DatastoreConfig, decorators: [{ type: Inject, args: [DATASTORE_CONFIG] }] }, { type: Auth }, { type: Store }, { type: NgZone }]; };
    return WebSocketService;
}());
export { WebSocketService };
//# sourceMappingURL=websocket.js.map


// WEBPACK FOOTER //
// websocket.js